// Document Studio — Brand Settings: edit the server-readable brand profile the
// catalog renderer uses (green, font, wordmark, company, channels). This is the
// real backend brand_profiles row, not the FE CSS tokens.

import { useState } from 'react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { toast } from '../../../lib/toast'
import {
  useBrandProfile,
  useUpdateBrand,
  type BrandProfileUpdate,
} from '../../../api/document-studio'
import { useT } from '../../../i18n'
import { documentStudioI18n } from '../../../i18n/admin/document-studio'

const inputClass = 'w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)]'

export function BrandSettingsPage() {
  const t = useT(documentStudioI18n).brandSettings
  const { data: brand, isLoading } = useBrandProfile()
  const updateMut = useUpdateBrand()
  const [form, setForm] = useState<BrandProfileUpdate>({})

  // Sync the form whenever a (new) brand profile arrives (adjust-during-render
  // with a previous-value comparison).
  const [prevBrand, setPrevBrand] = useState<typeof brand>(undefined)
  if (brand && brand !== prevBrand) {
    setPrevBrand(brand)
    setForm({
      company_name: brand.company_name,
      tagline: brand.tagline ?? '',
      brand_color: brand.brand_color,
      font_family: brand.font_family,
      wordmark_text: brand.wordmark_text,
      contact_email: brand.contact_email ?? '',
      website_url: brand.website_url ?? '',
      channels: brand.channels,
    })
  }

  function set<K extends keyof BrandProfileUpdate>(key: K, value: BrandProfileUpdate[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  async function save() {
    try {
      await updateMut.mutateAsync(form)
      toast.success(t.saved)
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  if (isLoading || !brand) {
    return <div className="p-6 text-sm text-[var(--color-text-muted)]">{t.loading}</div>
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <h1 className="text-xl font-semibold">{t.title}</h1>
      <p className="mb-4 text-sm text-[var(--color-text-muted)]">
        {t.subtitle}
      </p>

      <Card>
        <CardContent className="space-y-4 p-5">
          <Field label={t.companyName}>
            <input
              className={inputClass}
              value={form.company_name ?? ''}
              onChange={(e) => set('company_name', e.target.value)}
            />
          </Field>
          <Field label={t.tagline}>
            <input
              className={inputClass}
              value={form.tagline ?? ''}
              onChange={(e) => set('tagline', e.target.value)}
            />
          </Field>
          <div className="flex flex-col gap-4 sm:flex-row">
            <Field label={t.brandColor}>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="h-9 w-12 rounded border border-[var(--color-border-strong)]"
                  value={form.brand_color ?? '#16A34A'}
                  onChange={(e) => set('brand_color', e.target.value)}
                />
                <input
                  className={`${inputClass} max-w-28 px-2`}
                  value={form.brand_color ?? ''}
                  onChange={(e) => set('brand_color', e.target.value)}
                />
              </div>
            </Field>
            <Field label={t.wordmark}>
              <input
                className={inputClass}
                value={form.wordmark_text ?? ''}
                onChange={(e) => set('wordmark_text', e.target.value)}
              />
            </Field>
          </div>
          <Field label={t.fontFamily}>
            <input
              className={inputClass}
              value={form.font_family ?? ''}
              onChange={(e) => set('font_family', e.target.value)}
            />
          </Field>
          <div className="flex flex-col gap-4 sm:flex-row">
            <Field label={t.contactEmail}>
              <input
                className={inputClass}
                value={form.contact_email ?? ''}
                onChange={(e) => set('contact_email', e.target.value)}
              />
            </Field>
            <Field label={t.website}>
              <input
                className={inputClass}
                value={form.website_url ?? ''}
                onChange={(e) => set('website_url', e.target.value)}
              />
            </Field>
          </div>
          <Field label={t.channels}>
            <input
              className={inputClass}
              value={(form.channels ?? []).join(', ')}
              onChange={(e) =>
                set(
                  'channels',
                  e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                )
              }
            />
          </Field>

          <div className="flex justify-end">
            <Button onClick={save} disabled={updateMut.isPending}>
              {updateMut.isPending ? t.saving : t.save}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block flex-1">
      <span className="mb-1 block text-xs font-medium text-[var(--color-text-muted)]">{label}</span>
      {children}
    </label>
  )
}
